import {
  ReadonlyEventWatchable,
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { WebhookInstallStatus } from '@cotera/client/app/v1/webhook-providers/types';
import type { WatchablesFactory } from '../../watchables/watchables-factory';
import type { WebhookTriggerInstallState } from './types';
import type {
  WebhookTriggerPreviewState,
  WorkflowWebhookTriggerNodeModel,
} from './webhook-trigger-node.types';
import { WorkflowEntityNodeModel } from './workflow-entity-node-model';

type WorkflowWebhookTriggerFullNodeModelProps = {
  triggerId: string;
  initialInstallStatus?: WebhookInstallStatus;
  initialHasSecret?: boolean;
  client: Pick<TenantedClient, 'resource'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    'makeWebhookTriggerInstallStateWatchable'
  >;
};

function createInitialInstallState(
  installStatus: WebhookInstallStatus | undefined
): WebhookTriggerInstallState {
  return {
    installStatus: installStatus ?? 'draft',
    installError: null,
    loaded: installStatus !== undefined,
  };
}

/**
 * Persisted webhook trigger on the workflow canvas. Install status is kept
 * live via `workflow_trigger` resource events; secret/enabled reconcile from
 * the workflow graph when it changes.
 */
export class WorkflowWebhookTriggerFullNodeModel
  extends WorkflowEntityNodeModel
  implements WorkflowWebhookTriggerNodeModel
{
  readonly mode = 'full' as const;
  readonly triggerId: string;
  readonly installStateWatchable: ReadonlyWatchable<WebhookTriggerInstallState>;
  readonly installStatusWatchable: ReadonlyWatchable<WebhookInstallStatus>;
  readonly installErrorWatchable: ReadonlyWatchable<{
    code?: string;
    message?: string;
  } | null>;
  readonly hasSecretWatchable: ReadonlyWatchable<boolean>;

  private readonly _installStateEventWatchable: ReadonlyEventWatchable<WebhookTriggerInstallState>;
  private readonly _hasSecretWatchable: Watchable<boolean>;
  private readonly _dispose: () => void;

  constructor(props: WorkflowWebhookTriggerFullNodeModelProps) {
    super(props.triggerId);
    this.triggerId = props.triggerId;
    this._hasSecretWatchable = Watchable.fromValue(
      props.initialHasSecret ?? false
    );

    const eventWatchable =
      props.watchablesFactory.makeWebhookTriggerInstallStateWatchable({
        triggerId: props.triggerId,
        client: props.client,
        initialValue: createInitialInstallState(props.initialInstallStatus),
      });
    this._installStateEventWatchable = eventWatchable;
    this.installStateWatchable = eventWatchable;
    this._dispose = () => {
      eventWatchable.unsubscribe();
    };

    this.installStatusWatchable = Watchable.from(
      (get) => get(this.installStateWatchable).installStatus
    );
    this.installErrorWatchable = Watchable.from(
      (get) => get(this.installStateWatchable).installError
    );
    this.hasSecretWatchable = this._hasSecretWatchable;
  }

  reconcileInstallStatusFromGraph(
    installStatus: WebhookInstallStatus | undefined
  ): void {
    if (installStatus === undefined) {
      return;
    }
    const current = this.installStateWatchable.snapshot();
    if (current.loaded) {
      return;
    }
    const next = createInitialInstallState(installStatus);
    if (this.installStateWatchable instanceof Watchable) {
      this.installStateWatchable.set(next);
    }
  }

  reconcileFromGraph(params: {
    installStatus?: WebhookInstallStatus;
    hasSecret?: boolean;
  }): void {
    if (params.hasSecret !== undefined) {
      this._hasSecretWatchable.set(params.hasSecret);
    }
    this.reconcileInstallStatusFromGraph(params.installStatus);
  }

  syncFromGraph(preview: WebhookTriggerPreviewState): void {
    this.reconcileFromGraph({
      installStatus: preview.installStatus,
      hasSecret: preview.hasSecret,
    });
  }

  dispose(): void {
    this._dispose();
  }
}
